/**
 * Determines if a text node should be included in search/highlight operations
 * This logic must be consistent between search and highlight phases
 */
export const shouldIncludeTextNode = (textNode: Text): boolean => {
  // Skip empty text nodes
  if (!textNode.textContent?.trim()) {
    return false;
  }

  const parent = textNode.parentElement;
  if (!parent) {
    return false;
  }

  // Skip script and style tags
  if (['SCRIPT', 'STYLE'].includes(parent.tagName)) {
    return false;
  }

  // Skip if already inside a mark element (from previous highlights)
  if (parent.tagName === 'MARK') {
    return false;
  }

  // Skip text nodes inside tables with data-table-type attribute
  // These tables are searched via data-based search, not DOM search
  const closestTable = parent.closest('[data-table-type]');
  if (closestTable) {
    return false;
  }

  return true;
};

export const getSearchableTextNodes = (element: Element): Text[] => {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      return shouldIncludeTextNode(node as Text)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  let node: Node | null;
  while ((node = walker.nextNode())) {
    textNodes.push(node as Text);
  }

  return textNodes;
};

/**
 * Remove all search highlights and row highlights from a DOM element
 * @param element - The element to clean (can be a section or the entire container)
 */
export const cleanupHighlights = (element: Element): void => {
  // Remove all mark tags and track parent elements for normalization
  // But skip marks inside tables with data-table-type (those use data-based search)
  const allMarks = element.querySelectorAll('mark');
  const parentsToNormalize = new Set<Node>();

  allMarks.forEach((mark) => {
    // Skip marks inside tables with data-table-type attribute
    const closestTable = mark.closest('[data-table-type]');
    if (closestTable) {
      return;
    }

    const parent = mark.parentNode;
    const textNode = document.createTextNode(mark.textContent || '');
    parent?.replaceChild(textNode, mark);
    if (parent) {
      parentsToNormalize.add(parent);
    }
  });

  // Normalize each parent to merge adjacent text nodes
  parentsToNormalize.forEach((parent) => {
    (parent as HTMLElement).normalize();
  });

  // Remove all row highlights
  const highlightedRows = element.querySelectorAll('[data-search-row-highlight="true"]');
  highlightedRows.forEach((row) => {
    row.removeAttribute('data-search-row-highlight');
    row.classList.remove('search-highlight-row');
    (row as HTMLElement).style.backgroundColor = '';
  });
};
